/**
 * Analytics summary client (GET /api/v1/analytics/summary, CR-25): the control service
 * answers with the platform's precomputed daily aggregates - one dense day row per UTC day
 * of the requested range plus the totals over it. Authentication is the SPA's SSO session
 * bearer (the dashboard's session); guests (room JWT bearers) cannot read it. The server
 * composes and caches: the client only narrows.
 */

/** The range switcher's vocabulary - the server rejects anything else. */
export const ANALYTICS_RANGES = [7, 30, 90] as const;
export type AnalyticsRange = (typeof ANALYTICS_RANGES)[number];

/** The per-day metric set (definitions in control's 0018 migration header). */
export interface AnalyticsDay {
  /** UTC day as YYYY-MM-DD. */
  readonly date: string;
  readonly roomsCreated: number;
  readonly meetingsHeld: number;
  readonly webinarsHeld: number;
  readonly participantSessions: number;
  readonly participantMinutes: number;
  readonly recordingsCount: number;
  readonly invitesCreated: number;
  readonly moderationActions: number;
}

/** The metric set summed over the served range. */
export interface AnalyticsTotals {
  readonly roomsCreated: number;
  readonly meetingsHeld: number;
  readonly webinarsHeld: number;
  readonly participantSessions: number;
  readonly participantMinutes: number;
  readonly recordingsCount: number;
  readonly invitesCreated: number;
  readonly moderationActions: number;
}

/** The room kind of a top-rooms row (the ledger vocabulary; workshop reads as meeting). */
export type AnalyticsTopRoomKind = 'meeting' | 'webinar';

/**
 * One row of the summary's top-rooms table: the per-room substance behind the daily
 * aggregates. Ledger metrics are honestly zero for pre-ledger rooms; recordings and invites
 * are exact for the whole range. Ordered by the server (sessions desc), capped server-side.
 */
export interface AnalyticsTopRoom {
  readonly roomSlug: string;
  readonly kind: AnalyticsTopRoomKind;
  readonly sessions: number;
  readonly minutes: number;
  readonly recordings: number;
  readonly invites: number;
  /** The room's freshest fact stamp inside the range, as an ISO-8601 UTC string. */
  readonly lastActivity: string;
}

/** The body of GET /api/v1/analytics/summary. */
export interface AnalyticsSummary {
  readonly range: number;
  readonly days: readonly AnalyticsDay[];
  readonly totals: AnalyticsTotals;
  readonly topRooms: readonly AnalyticsTopRoom[];
}

/** Error of the analytics calls, carrying the contract envelope fields. */
export class AnalyticsApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'AnalyticsApiError';
    this.status = status;
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrows a non-negative finite metric value; anything else fails. */
function metric(value: unknown, field: string): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }
  throw new TypeError(`analytics ${field} must be a non-negative number`);
}

/** Narrows one day row; the date must be a YYYY-MM-DD string. */
export function parseAnalyticsDay(value: unknown): AnalyticsDay {
  if (!isRecord(value)) {
    throw new TypeError('analytics day must be an object');
  }
  const date = typeof value.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.date) ? value.date : null;
  if (date === null) {
    throw new TypeError('analytics day date must be YYYY-MM-DD');
  }
  return {
    date,
    roomsCreated: metric(value.roomsCreated, 'roomsCreated'),
    meetingsHeld: metric(value.meetingsHeld, 'meetingsHeld'),
    webinarsHeld: metric(value.webinarsHeld, 'webinarsHeld'),
    participantSessions: metric(value.participantSessions, 'participantSessions'),
    participantMinutes: metric(value.participantMinutes, 'participantMinutes'),
    recordingsCount: metric(value.recordingsCount, 'recordingsCount'),
    invitesCreated: metric(value.invitesCreated, 'invitesCreated'),
    moderationActions: metric(value.moderationActions, 'moderationActions'),
  };
}

/** Narrows the totals section. */
export function parseAnalyticsTotals(value: unknown): AnalyticsTotals {
  if (!isRecord(value)) {
    throw new TypeError('analytics totals must be an object');
  }
  return {
    roomsCreated: metric(value.roomsCreated, 'totals.roomsCreated'),
    meetingsHeld: metric(value.meetingsHeld, 'totals.meetingsHeld'),
    webinarsHeld: metric(value.webinarsHeld, 'totals.webinarsHeld'),
    participantSessions: metric(value.participantSessions, 'totals.participantSessions'),
    participantMinutes: metric(value.participantMinutes, 'totals.participantMinutes'),
    recordingsCount: metric(value.recordingsCount, 'totals.recordingsCount'),
    invitesCreated: metric(value.invitesCreated, 'totals.invitesCreated'),
    moderationActions: metric(value.moderationActions, 'totals.moderationActions'),
  };
}

/** Narrows one top-rooms row: the slug must be non-empty, the kind part of the vocabulary. */
export function parseAnalyticsTopRoom(value: unknown): AnalyticsTopRoom {
  if (!isRecord(value)) {
    throw new TypeError('analytics top room must be an object');
  }
  const roomSlug = typeof value.roomSlug === 'string' && value.roomSlug.length > 0 ? value.roomSlug : null;
  if (roomSlug === null) {
    throw new TypeError('analytics top room roomSlug must be a non-empty string');
  }
  const kind: AnalyticsTopRoomKind | null =
    value.kind === 'meeting' ? 'meeting' : value.kind === 'webinar' ? 'webinar' : null;
  if (kind === null) {
    throw new TypeError('analytics top room kind must be meeting or webinar');
  }
  const lastActivity =
    typeof value.lastActivity === 'string' &&
    value.lastActivity.length > 0 &&
    !Number.isNaN(Date.parse(value.lastActivity))
      ? value.lastActivity
      : null;
  if (lastActivity === null) {
    throw new TypeError('analytics top room lastActivity must be an ISO date string');
  }
  return {
    roomSlug,
    kind,
    sessions: metric(value.sessions, 'topRoom.sessions'),
    minutes: metric(value.minutes, 'topRoom.minutes'),
    recordings: metric(value.recordings, 'topRoom.recordings'),
    invites: metric(value.invites, 'topRoom.invites'),
    lastActivity,
  };
}

/**
 * Narrows the summary response body ({range, days, totals, topRooms}). The top level is
 * strict: the composition is one server-side cached view, a half-true summary would be worse
 * than a visible error.
 */
export function parseAnalyticsSummary(body: unknown): AnalyticsSummary {
  if (!isRecord(body)) {
    throw new TypeError('analytics summary must be an object');
  }
  const range =
    typeof body.range === 'number' && Number.isInteger(body.range) && body.range > 0
      ? body.range
      : null;
  if (range === null || !Array.isArray(body.days) || !Array.isArray(body.topRooms)) {
    throw new TypeError('analytics summary must carry a range, a days array and a topRooms array');
  }
  return {
    range,
    days: body.days.map((day) => parseAnalyticsDay(day)),
    totals: parseAnalyticsTotals(body.totals),
    topRooms: body.topRooms.map((room) => parseAnalyticsTopRoom(room)),
  };
}

/** True when the value is one of the range switcher's options. */
export function isAnalyticsRange(value: number): value is AnalyticsRange {
  return (ANALYTICS_RANGES as readonly number[]).includes(value);
}

/**
 * GET /api/v1/analytics/summary?days=N with the SSO bearer: the cached daily aggregates for
 * the range. The server recomputes on demand when its cached day is stale, so the client
 * never waits for a recompute it cannot see.
 */
export async function fetchAnalyticsSummary(
  token: string,
  days: AnalyticsRange,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<AnalyticsSummary> {
  let response: Response;
  try {
    response = await fetchImpl(`/api/v1/analytics/summary?days=${String(days)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new AnalyticsApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    let code = 'unknown_error';
    let message = `analytics request failed with HTTP ${String(response.status)}`;
    try {
      const body: unknown = await response.json();
      if (isRecord(body) && isRecord(body.error)) {
        if (typeof body.error.code === 'string') {
          code = body.error.code;
        }
        if (typeof body.error.message === 'string') {
          message = body.error.message;
        }
      }
    } catch {
      // non-JSON error body, keep the generic message
    }
    throw new AnalyticsApiError(response.status, code, message);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new AnalyticsApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  try {
    return parseAnalyticsSummary(body);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed analytics response';
    throw new AnalyticsApiError(response.status, 'invalid_response', detail);
  }
}
